(() => {
	// Starts in normal flow, right below the announcement bar (like the default header).
	// Only once scrolled past the header's own natural position does it switch to
	// position:fixed (the .is-pinned class) — this keeps the announcement bar visible and
	// in place until the user actually scrolls past the header, then lets it scroll away
	// while the header stays pinned, matching the default header's own behavior. Plain
	// position:sticky doesn't stick here at all (see the comment above the spacer div in
	// custom-header.liquid), hence doing the pin/unpin toggle by hand.
	function initSticky(header) {
		if (header.dataset.stickyInitialized) return;
		header.dataset.stickyInitialized = "true";

		if (header.getAttribute("data-sticky") !== "true") return;
		const spacer = header.nextElementSibling;
		if (!spacer || !spacer.classList.contains("custom-header__spacer")) return;

		let naturalOffsetTop = null;

		// Exposes the header's current rendered height as a CSS variable so other sections
		// (e.g. sections/custom-product.liquid's sticky tabs bar) can position themselves
		// just below it without hardcoding a height — this header's own height varies with
		// settings (top menu bar on/off, logo size, etc.), unlike the reference header.liquid,
		// which sets its own --header-height variable that this custom header never touches.
		function updateHeaderHeightVar() {
			document.documentElement.style.setProperty("--custom-header-height", `${header.offsetHeight}px`);
		}

		function captureNaturalOffset() {
			if (header.classList.contains("is-pinned")) return;
			naturalOffsetTop = header.getBoundingClientRect().top + window.scrollY;
		}

		function updatePinned() {
			if (naturalOffsetTop === null) return;
			const shouldPin = window.scrollY > naturalOffsetTop;
			const isPinned = header.classList.contains("is-pinned");
			if (shouldPin !== isPinned) {
				if (shouldPin) {
					spacer.style.height = `${header.offsetHeight}px`;
					header.classList.add("is-pinned");
				} else {
					header.classList.remove("is-pinned");
					spacer.style.height = "0px";
					captureNaturalOffset();
				}
			}
			updateHeaderHeightVar();
		}

		captureNaturalOffset();
		updateHeaderHeightVar();
		updatePinned();

		window.addEventListener("scroll", updatePinned, { passive: true });
		window.addEventListener("resize", () => {
			captureNaturalOffset();
			updatePinned();
		});
	}

	function initDrawer(header) {
		const toggle = header.querySelector(".custom-header__icon--menu-toggle");
		const drawer = header.querySelector(".custom-header__drawer");
		if (!toggle || !drawer || drawer.dataset.initialized) return;
		drawer.dataset.initialized = "true";

		const closeButton = drawer.querySelector(".custom-header__drawer-close");
		const panel = drawer.querySelector(".custom-header__drawer-panel");

		function openDrawer() {
			drawer.hidden = false;
			requestAnimationFrame(() => drawer.classList.add("is-open"));
			toggle.setAttribute("aria-expanded", "true");
			document.body.style.overflow = "hidden";
		}

		function closeDrawer() {
			drawer.classList.remove("is-open");
			toggle.setAttribute("aria-expanded", "false");
			document.body.style.overflow = "";
			setTimeout(() => {
				drawer.hidden = true;
			}, 300);
		}

		toggle.addEventListener("click", openDrawer);
		closeButton && closeButton.addEventListener("click", closeDrawer);
		drawer.addEventListener("click", event => {
			if (panel && !panel.contains(event.target)) closeDrawer();
		});
		document.addEventListener("keydown", event => {
			if (event.key === "Escape" && !drawer.hidden) closeDrawer();
		});
	}

	// Same open/close mechanism as assets/header.js's search: toggling the
	// .header__search-inner--open class (base.css drives the opacity/visibility
	// transition off that class, not an hidden attribute) plus body overflow lock.
	function initSearchPanel(header) {
		const toggle = header.querySelector(".header__search .header__icon--search");
		const panel = header.querySelector(".header__search-inner");
		if (!toggle || !panel || panel.dataset.initialized) return;
		panel.dataset.initialized = "true";

		const input = panel.querySelector(".search__input");

		function isOpen() {
			return panel.classList.contains("header__search-inner--open");
		}

		function openPanel() {
			panel.classList.add("header__search-inner--open");
			document.body.classList.add("overflow-hidden-search");
			toggle.setAttribute("aria-expanded", "true");
			input && input.focus();
		}

		function closePanel() {
			panel.classList.remove("header__search-inner--open");
			document.body.classList.remove("overflow-hidden-search");
			toggle.setAttribute("aria-expanded", "false");
		}

		function toggleOpen() {
			if (isOpen()) closePanel();
			else openPanel();
		}

		toggle.addEventListener("click", toggleOpen);
		toggle.addEventListener("keydown", event => {
			if (event.key === "Enter" || event.key === " ") {
				event.preventDefault();
				toggleOpen();
			}
		});
		panel.addEventListener("click", event => {
			if (event.target === panel) closePanel();
		});
		document.addEventListener("keydown", event => {
			if (event.key === "Escape" && isOpen()) closePanel();
		});
	}

	function initNavToggles(header) {
		const items = Array.from(header.querySelectorAll(".list-menu-item.list-menu--submenu"));
		if (items.length === 0 || header.dataset.navInitialized) return;
		header.dataset.navInitialized = "true";

		function closeItem(item) {
			item.classList.remove("is-open");
		}

		function toggleItem(item) {
			const isOpen = item.classList.contains("is-open");
			items.forEach(other => {
				if (other !== item) closeItem(other);
			});
			item.classList.toggle("is-open", !isOpen);
		}

		items.forEach(item => {
			const caret = item.querySelector(".header__menu-item svg");
			if (!caret) return;
			caret.addEventListener("click", event => {
				event.preventDefault();
				event.stopPropagation();
				toggleItem(item);
			});
		});

		document.addEventListener("click", event => {
			if (!header.contains(event.target)) {
				items.forEach(closeItem);
				return;
			}
			items.forEach(item => {
				if (!item.contains(event.target)) closeItem(item);
			});
		});

		document.addEventListener("keydown", event => {
			if (event.key === "Escape") items.forEach(closeItem);
		});
	}

	// Sliding accent-colored highlight that tracks the hovered/active nav item — same
	// #blob element/technique as assets/header.js, adapted to this section's own nav
	// (base.css already styles #blob via ".header__inline-menu #blob").
	function initBlob(header) {
		const track = header.querySelector(".custom-header__nav .list-menu--inline");
		const blob = header.querySelector(".custom-header__nav #blob");
		if (!track || !blob || track.dataset.blobInitialized) return;
		track.dataset.blobInitialized = "true";

		const activeItem = track.querySelector(".list-menu-item--active");
		let hasBlobPosition = false;

		function makeBlob(target) {
			if (!target) return;
			blob.style.width = `${target.clientWidth}px`;
			blob.style.height = `${target.clientHeight}px`;
			blob.style.left = `${target.offsetLeft}px`;
			blob.style.top = `${target.offsetTop}px`;
			blob.style.opacity = "1";
			blob.style.backgroundColor = "rgb(var(--color-accent))";
		}

		function hideBlob() {
			blob.style.backgroundColor = "transparent";
		}

		// Reads the blob's LIVE layout box (offsetLeft/offsetTop/clientWidth), not its
		// .style.* strings — those only ever hold whatever makeBlob() last wrote and are
		// never updated by a running Web Animation, so using them as the "from" keyframe
		// made every hover animate from a stale position (always the previous target)
		// instead of from wherever the blob visually is right now.
		function animate(target) {
			if (!target) return;
			blob.animate(
				[
					{ left: `${blob.offsetLeft}px`, width: `${blob.offsetWidth}px`, top: `${blob.offsetTop}px`, opacity: "1" },
					{ left: `${target.offsetLeft}px`, width: `${target.clientWidth}px`, top: `${target.offsetTop}px`, opacity: "1" },
				],
				{ duration: 300, easing: "cubic-bezier(0.87, 0, 0.13, 1)", fill: "forwards" },
			);
		}

		if (activeItem) {
			makeBlob(activeItem);
			hasBlobPosition = true;
		} else {
			blob.style.opacity = "0";
			hideBlob();
		}

		track.addEventListener("mouseover", event => {
			const targetItem = event.target.closest(".list-menu-item");
			if (!targetItem || targetItem.id === "blob") return;

			blob.style.backgroundColor = "rgb(var(--color-accent))";

			if (!hasBlobPosition) {
				makeBlob(targetItem);
				blob.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300, easing: "ease-out", fill: "forwards" });
				hasBlobPosition = true;
				return;
			}

			animate(targetItem);
		});

		track.addEventListener("mouseleave", () => {
			if (activeItem) animate(activeItem);
			else hideBlob();
		});

		window.addEventListener("resize", () => {
			if (activeItem) makeBlob(activeItem);
		});
	}

	// Mega menu is position:fixed (base.css's own .list-menu--inline .list-menu-item rule
	// puts position:relative on every nav <li>, so position:absolute would anchor to that
	// narrow item instead of the full header — fixed positioning sidesteps it entirely).
	// Since "fixed" needs real numbers, not percentages, top/left/width are computed here
	// from the header's own bounding box each time a panel is about to open. At 960px and
	// up the panel matches the header's own .container (same left/right edges as the logo
	// and nav content); the nav is hidden below that breakpoint entirely (replaced by the
	// hamburger drawer), so the full-width fallback there is mostly a formality.
	function initMegaMenuPositioning(header) {
		const items = Array.from(header.querySelectorAll(".list-menu-item.has-mega-menu"));
		if (items.length === 0 || header.dataset.megaMenuPositioningInitialized) return;
		header.dataset.megaMenuPositioningInitialized = "true";

		const container = header.querySelector(".custom-header__main-inner");

		function positionPanel(panel) {
			const headerRect = header.getBoundingClientRect();
			const useContainer = window.innerWidth >= 960 && container;
			const rect = useContainer ? container.getBoundingClientRect() : headerRect;
			panel.style.top = `${headerRect.bottom}px`;
			panel.style.left = `${rect.left}px`;
			panel.style.width = `${rect.width}px`;
		}

		items.forEach(item => {
			const panel = item.querySelector(".custom-header__mega-menu");
			if (!panel) return;
			item.addEventListener("mouseenter", () => positionPanel(panel));
			item.addEventListener("focusin", () => positionPanel(panel));
			// Touch fallback: initNavToggles() opens mega items via .is-open on caret tap,
			// with no preceding mouseenter/focusin, so position on click too.
			item.addEventListener("click", () => positionPanel(panel));
		});

		window.addEventListener("resize", () => {
			items.forEach(item => {
				const panel = item.querySelector(".custom-header__mega-menu");
				panel && positionPanel(panel);
			});
		});
	}

	function initHeader(header) {
		initSticky(header);
		initDrawer(header);
		initSearchPanel(header);
		initNavToggles(header);
		initBlob(header);
		initMegaMenuPositioning(header);
	}

	function initAllHeaders(root = document) {
		root.querySelectorAll(".custom-header").forEach(initHeader);
	}

	document.addEventListener("DOMContentLoaded", () => initAllHeaders());
	document.addEventListener("shopify:section:load", event => initAllHeaders(event.target));
	if (document.readyState !== "loading") initAllHeaders();
})();
